import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import { parseNumber } from "../../utils/parseNumber";

export const useActions = ({
    workspace,
    confirmDisable,
    modalScraping,
    scrapingDetails,
    rowsToDisplay,
    disableActiveMode,
    disableScraping,
    addScraping,
    updateMaxCpc,
    enforceMaxCPC,
    disableKoBiddingManualMaxCpc,
    disableKoBiddingAutoMaxCpc,
    setLoading,
    setLocalPatches,
    setModifiedRows,
    setConfirmDisable,
    setModalScraping,
    handleCloseScraping
}: any) => {

    const handleDisableActiveMode = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableActiveMode({
                    id: detail.id,
                    workspace_id: workspace.id,
                }).unwrap();

                enqueueSnackbar(`Active Mode disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches((prev: any) => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        activations_details: rowToPatch.activations_details.filter(
                            (d: any) => d.mode !== "Active Mode"
                        )
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Active Mode`, { variant: "error" });
            }
        },
        [
            disableActiveMode,
            workspace,
            confirmDisable?.row,
            setLocalPatches,
        ]
    );

    const handleDisableScraping = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableScraping({
                    id: detail.id,
                }).unwrap();

                enqueueSnackbar(
                    "Scraping disabled successfully",
                    { variant: "success" }
                );

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches((prev: any) => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        scraping_details: [],
                    },
                }));
            } catch (err: any) {
                console.error(err);

                enqueueSnackbar(
                    "Failed to disable Scraping",
                    { variant: "error" }
                );
            }
        },
        [
            disableScraping,
            workspace,
            confirmDisable?.row,
            setLocalPatches,
        ]
    );

    const handleConfirmDisable = useCallback(
        async (row: any) => {
            if (!row) return;

            const modeToDisable = confirmDisable.mode;

            try {
                if (modeToDisable === "Active Mode") {
                    const detail = row.activations_details?.find(
                        (d: any) =>
                            d.mode === "Active Mode" &&
                            d.status === true
                    );

                    await handleDisableActiveMode(detail);
                }

                else if (modeToDisable === "Scraping") {
                    const detail = row.scraping_details?.[0];

                    await handleDisableScraping(detail);
                }

                else if (modeToDisable === "KO Bidding Manual") {
                    const detail = row.activations_details?.find(
                        (d: any) =>
                            d.mode === "KO Bidding Manual" &&
                            d.status === true
                    );

                    if (detail?.id) {
                        await disableKoBiddingManualMaxCpc({
                            id: detail.id,
                        }).unwrap();

                        enqueueSnackbar(
                            `Activation disabled successfully`,
                            { variant: "success" }
                        );

                        setLocalPatches((prev: any) => {
                            const existing = prev[row.id] ?? row;

                            const nextActivations =
                                existing.activations_details.filter(
                                    (d: any) =>
                                        d.mode !== "KO Bidding Manual"
                                );

                            const koManual =
                                nextActivations.find(
                                    (a: any) =>
                                        a.status &&
                                        a.mode === "KO Bidding Manual"
                                );

                            return {
                                ...prev,
                                [row.id]: {
                                    ...existing,
                                    activations_details:
                                        nextActivations,
                                    max_cpc:
                                        koManual?.max_cpc ?? null,
                                    mode:
                                        nextActivations?.[0]?.mode ??
                                        null,
                                },
                            };
                        });
                    }
                }

                else if (
                    modeToDisable?.startsWith(
                        "KO Bidding Auto"
                    )
                ) {
                    const detail =
                        row.activations_details?.find(
                            (d: any) =>
                                d.mode?.startsWith(
                                    "KO Bidding Auto"
                                ) &&
                                d.status === true
                        );

                    if (detail?.id) {
                        await disableKoBiddingAutoMaxCpc({
                            id: detail.id,
                        }).unwrap();

                        enqueueSnackbar(
                            `Activation disabled successfully`,
                            {
                                variant: "success",
                            }
                        );

                        setLocalPatches((prev: any) => ({
                            ...prev,
                            [row.id]: {
                                ...row,
                                activations_details:
                                    row.activations_details.filter(
                                        (d: any) =>
                                            !d.mode?.startsWith(
                                                "KO Bidding Auto"
                                            )
                                    ),
                                max_cpc: null,
                            },
                        }));
                    }
                }

                setConfirmDisable({
                    open: false,
                    row: null,
                    mode: null,
                });
            } catch (err) {
                enqueueSnackbar(
                    `Failed to disable ${modeToDisable}`,
                    {
                        variant: "error",
                    }
                );
            }
        },
        [
            confirmDisable,
            handleDisableActiveMode,
            handleDisableScraping,
            disableKoBiddingManualMaxCpc,
            disableKoBiddingAutoMaxCpc,
            setLocalPatches,
            setConfirmDisable
        ]
    );

    const handleConfirmScraping = useCallback(
        async (rowsToDelete: Set<any>) => {
            const rows = modalScraping.row;

            if (!rows || !workspace) return;

            setLoading?.(true);

            try {
                const details =
                    scrapingDetails.length > 0
                        ? scrapingDetails
                        : rowsToDisplay;

                const activeRows = details.filter(
                    (row: any) => !rowsToDelete.has(row.id)
                );

                const payloadArray = activeRows.map(
                    (detail: any, index: number) => {
                        const originalRow =
                            rows[index] ?? rows[0];

                        return {
                            keyword:
                                detail.keyword ??
                                originalRow.keyword,

                            country: Array.isArray(
                                detail.country
                            )
                                ? detail.country
                                : [detail.country],

                            country_code: [
                                originalRow.country_code[index],
                            ],

                            home_language:
                                originalRow.home_language[0],

                            frequency: "daily",

                            platform_scraping:
                                Array.isArray(
                                    detail.platform_scraping
                                )
                                    ? detail.platform_scraping
                                    : [
                                        detail.platform_scraping ??
                                        "Google",
                                    ],

                            device:
                                detail.device ?? [
                                    "desktop",
                                    "mobile",
                                ],

                            workspaceId: workspace.id,
                            client: workspace.bqTable,
                            mode: "Scraping",
                        };
                    }
                );

                const result =
                    await addScraping(
                        payloadArray
                    ).unwrap();

                enqueueSnackbar(
                    "Scraping added successfully.",
                    {
                        variant: "success",
                    }
                );

                rows.forEach((r: any) => {
                    setLocalPatches(
                        (prev: any) => ({
                            ...prev,
                            [r.id]: {
                                ...prev[r.id],
                                scraping_details:
                                    result.map(
                                        (
                                            newScraping: any
                                        ) => ({
                                            ...newScraping,
                                            status: true,
                                        })
                                    ),
                            },
                        })
                    );
                });

                handleCloseScraping();
            } catch (err) {
                console.error(err);

                enqueueSnackbar(
                    "Failed to add scraping",
                    {
                        variant: "error",
                    }
                );
            } finally {
                setLoading?.(false);
            }
        },
        [
            modalScraping?.row,
            workspace,
            scrapingDetails,
            rowsToDisplay,
            addScraping,
            setLocalPatches,
            handleCloseScraping,
            setLoading,
        ]
    );

    const handleUpdateMaxCPC = useCallback(
        async (modifiedRows: any[]) => {

            try {
                const payload = modifiedRows.map(
                    (row) => ({
                        id: row.id,
                        workspaceId: workspace?.id,
                        max_cpc: Number(
                            row.max_cpc
                        ),
                    })
                );

                await updateMaxCpc(
                    payload
                ).unwrap();

                setLocalPatches((prev: any) => {
                    const next = { ...prev };

                    for (const row of modifiedRows) {
                        next[row.rowId] = {
                            ...next[row.rowId],
                            max_cpc: row.max_cpc,
                            isEdited: false,
                            isInvalid: false,
                        };
                    }

                    return next;
                });

                setModifiedRows([]);

                const count =
                    modifiedRows.length;

                enqueueSnackbar(
                    `Update${count > 1 ? "s" : ""
                    } applied successfully`,
                    {
                        variant: "success",
                    }
                );
            } catch (error) {
                console.error(
                    "Update failed:",
                    error
                );

                setLocalPatches({});
                setModifiedRows([]);
            }
        },
        [
            workspace?.id,
            updateMaxCpc,
            setLocalPatches,
            setModifiedRows,
        ]
    );

    const handleFinish = useCallback(
        async (
            rows: any[],
            workspaceId: number,
            setResponse: (res: any[]) => void
        ) => {
            const payload = {
                workspaceId,
                keywords: rows.map(
                    (row) => ({
                        customer_id: String(
                            row.account_id
                        ),

                        account_id: String(
                            row.account_id
                        ),

                        ad_group_id: String(
                            row.ad_group_ids[0]
                        ),

                        platform:
                            row?.platform ===
                                "GOOGLE"
                                ? "Google Ads"
                                : row?.platform ===
                                    "BING"
                                    ? "Bing Ads"
                                    : row?.platform,

                        keyword_id: String(
                            row.keyword_id
                        ),

                        keyword: row.keyword,

                        match_type:
                            row.match_type,

                        campaign_id: String(
                            row.campaign_ids[0]
                        ),

                        max_cpc:
                            row.max_cpc_to_enforce,

                        name:
                            workspace?.bqTable,

                        country_code:
                            row.country_code,

                        message:
                            "Enforce max CPC from front-end",
                    })
                ),
            };

            try {
                const response =
                    await enforceMaxCPC(
                        payload
                    ).unwrap();

                setResponse(response);
            } catch (error) {
                console.error(
                    "Enforce error",
                    error
                );
            }
        },
        [enforceMaxCPC, workspace?.bqTable]
    );

    const handleToggle = useCallback((row: any, currentStatus: boolean) => {
        if (currentStatus) {
            setConfirmDisable({ open: true, row, mode: "Scraping" });
        } else {
            setModalScraping({
                open: true,
                row: [row],
            });
        }
    }, []);

    const handleRowUpdate = (newRow: any, oldRow: any) => {
        const raw = newRow.max_cpc;

        const nextValue = parseNumber(raw);
        const prevValue = parseNumber(oldRow.max_cpc);

        if (nextValue === prevValue) {
            return oldRow;
        }

        const koManual = newRow.activations_details?.find(
            (d: any) => d.mode === "KO Bidding Manual" && d.status === true
        );

        const isInvalid =
            koManual && (nextValue === null || nextValue < 0.01);

        const updatedRow = {
            ...newRow,
            isEdited: true,
            isInvalid,
        };

        if (koManual) {
            setModifiedRows((prev: any) => {
                const filtered = prev.filter((r: any) => r.rowId !== newRow.id);

                if (!isInvalid) {
                    return [
                        ...filtered,
                        {
                            rowId: newRow.id,
                            id: koManual.id,
                            max_cpc: nextValue,
                        }
                    ];
                }

                return filtered;
            });
        }

        return updatedRow;
    };

    return {
        handleDisableActiveMode,
        handleDisableScraping,
        handleConfirmDisable,
        handleConfirmScraping,
        handleUpdateMaxCPC,
        handleFinish,
        handleToggle,
        handleRowUpdate
    };
};